import { BusinessObjectBase } from './base';
import { resourceSchema } from '@schemas/business-objects/resource';
import type { Image } from './image';
import type { PdfDocument } from './pdf-document';
import type { ResourceCategoryItem } from './resource-category-item';
import type { ResourceSentence } from './resource-sentence';
import type { SessionActivityItem } from './session-activity-item';
import type { TextBox } from './text-box';
import type { User } from './user';
import type { Id, ResourceType } from '../base-types';
import type { ExportImportConfig } from '../interface-definitions/index';

export interface ResourceData {
  readonly id: Id;
  readonly name?: string;
  readonly type?: ResourceType;
  readonly instructions?: string;
  readonly categoryItems?: ResourceCategoryItem[];
  readonly pdfDocument?: PdfDocument;
  readonly images?: Image[];
  readonly sessionActivityItems?: SessionActivityItem[];
  readonly textBoxes?: TextBox[];
  readonly sentences?: ResourceSentence[];
  readonly createdById: Id;
  readonly createdBy?: User;
  readonly publishedById: Id;
  readonly publishedBy?: User;
  readonly archivedById: Id;
  readonly archivedBy?: User;
  readonly locked?: boolean;
  readonly uuid?: string;
  readonly version?: number;
  readonly status?: string;
  readonly createdAt?: Date;
  readonly publishedAt?: Date;
  readonly archivedAt?: Date;
}

export interface Resource extends Readonly<ResourceData> {}
type ResourceCreateWithChildrenData = Partial<
  Omit<ResourceData, 'images' | 'textBoxes' | 'sentences'>
> & {
  images?: Partial<Image>[];
  textBoxes?: Partial<TextBox>[];
  sentences?: Partial<ResourceSentence>[];
};

export class Resource extends BusinessObjectBase {
  static readonly entityName = 'Resource' as const;
  static readonly plural = 'resources';
  static schema = resourceSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Resource[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Resource(data));
  }

  static async findOne(filter?: any): Promise<Resource | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Resource(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Resource> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Resource(data);
  }

  static async create(data: Omit<ResourceData, 'id'>): Promise<Resource> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Resource(result);
  }

  static async createMany(
    data: Omit<ResourceData, 'id'>[],
  ): Promise<Resource[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Resource(item));
  }

  static async createWithChildren(
    data: ResourceCreateWithChildrenData,
  ): Promise<Resource> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new Resource(result);
  }

  static async createManyWithChildren(
    data: ResourceCreateWithChildrenData[],
  ): Promise<Resource[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Resource(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ResourceData>,
  ): Promise<Resource> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Resource(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async export(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Resource.plural}/${(this as any).id}/export`;
    return BusinessObjectBase.get<any>(url);
  }
  static async exportMany(body: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/export-many`;
    return this.post<any>(url, body);
  }
  async publish(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Resource.plural}/${(this as any).id}/publish`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async newVersion(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Resource.plural}/${(this as any).id}/new-version`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async latestVersion(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Resource.plural}/${(this as any).id}/latest-version`;
    return BusinessObjectBase.get<any>(url);
  }
  static async findLatestVersions(
    filter: any,
    publishedOnly?: boolean,
  ): Promise<any[]> {
    const url = `${this.baseUrl}/${this.plural}/find-latest-versions`;
    const queryParams = new URLSearchParams();
    if (filter !== undefined && filter !== null)
      queryParams.set('filter', JSON.stringify(filter));
    if (publishedOnly !== undefined && publishedOnly !== null)
      queryParams.set('publishedOnly', String(publishedOnly));
    const qs = queryParams.toString();
    const fullUrl = qs ? `${url}?${qs}` : url;
    return this.get<any[]>(fullUrl);
  }
  async archive(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Resource.plural}/${(this as any).id}/archive`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async dataForCopy(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Resource.plural}/${(this as any).id}/data-for-copy`;
    return BusinessObjectBase.post<any>(url, {});
  }
}
